import { useEffect, useLayoutEffect, useRef, useState } from 'react'

// Must match `$dock-duration` in VoiceAgents.scss. A dock unmounts only after
// its slide-out has finished, so the panel is still in the DOM to animate.
export const DOCK_EXIT_MS = 340

// Mount/enter bookkeeping for a panel that slides a column open and closed.
//
// Returns:
//   `mounted` — render at all. Stays true through the exit animation, which is
//               the whole reason a plain `{isOpen && <Panel/>}` can't do this.
//   `entered` — the `--open` class.
//   `ref`     — must be attached to the element that carries `--open`. The
//               opening animation depends on measuring it; see below.
export const useSlideDock = (isOpen) => {
  const [mounted, setMounted] = useState(false)
  const [entered, setEntered] = useState(false)
  const ref = useRef(null)

  // Mount and unmount. Closing also clears `entered` here, in an ordinary
  // render — that direction animates on its own, because the element is already
  // on screen in its open state for the transition to start from.
  useEffect(() => {
    if (isOpen) {
      setMounted(true)
      return undefined
    }
    setEntered(false)
    const timer = setTimeout(() => setMounted(false), DOCK_EXIT_MS)
    return () => clearTimeout(timer)
  }, [isOpen])

  // Opening is the hard direction. A transition fires when a property's
  // computed value changes *between two style recalculations* — so the
  // collapsed geometry has to be resolved once, on its own, before the class
  // flips. Mount and flip in one recalculation and the browser only ever sees
  // the end state: the panel appears already open.
  //
  // The `getBoundingClientRect()` below is the whole fix. It forces that first
  // recalculation now, with the element still at width 0, which becomes the
  // value the transition interpolates from. A layout effect is the right place
  // for it: the DOM is committed but nothing has painted yet.
  //
  // Scheduling the flip in `requestAnimationFrame` instead is the usual
  // suggestion and is not reliable here — a passive effect can run in the same
  // frame its commit is painted in, so the callback may still land before that
  // paint and both states end up in one recalculation anyway. That is the bug
  // this replaced: closing animated, opening popped.
  useLayoutEffect(() => {
    if (!mounted || !isOpen || entered) {
      return
    }
    if (ref.current) {
      ref.current.getBoundingClientRect()
    }
    setEntered(true)
  }, [mounted, isOpen, entered])

  return { mounted, entered, ref }
}
